import { API_URL } from "@/app.conf"
import { Base64 } from 'js-base64'




// API resource names
export const RESOURCE_NAMES = {
	accounts: 'accounts',
	organizations: 'organizations',
	stationPassports: 'stations',
	postPassports: 'posts',
	rpc: 'rpc',
	reports: 'reports',
	sessions: 'sessions',
	lightnings: 'lightnings',
	events: 'events',
}

export const resourceSchema = Object.freeze({
	updateTime: Object.freeze({
		key: "_updated",
	}),
	createTime: Object.freeze({
		key: "_created",
	}),
	id: Object.freeze({
		key: "_id",
	}),
	etag: Object.freeze({
		key: "_etag",
	}),
	version: Object.freeze({
		key: '_version',
	}),
	latestVersion: Object.freeze({
		key: '_latest_version',
	}),
	links: Object.freeze({
		key: '_links',
	}),
})
export const FIELD_NAMES = Object.fromEntries(
	Object.entries(resourceSchema).map(([key, value]) => [key, value.key])
)

export const API_PAGE_ITEMS_COUNT = 25
// ID of blank organization with default api keys
export const BLANK_ORGANIZATION_ID = '677cd9ba302ae22e2dac281d'




export function convertToApiTimeDate(raw) {
	const td = raw instanceof Date ? raw : new Date(raw)
	return td.toISOString().slice(0,-1) + "000"
}

/* Parse API date string to js Date Object */
export function parseApiTimeDate(raw) {
	if (typeof raw == 'string') {
		return new Date(Date.parse(raw) + (new Date()).getTimezoneOffset() * 60 * 1000 * -1)
	} else {
		console.error(`need String type input instead of ${typeof raw}`, raw)
		return
	}
}

// Parse data fields from DB to Data() objects
export function parseResource(item, dateFields) {
	const result = structuredClone(item)

	for (const path of dateFields) {
		const raw = getByPath(result, path)
		if (typeof raw === 'string') {
			setByPath(result, path, parseApiTimeDate(raw))
		}
	}

	return result
}

// Formts data field from Data() objects to DB time format
export function formatResource(item, dateFields) {
	const result = structuredClone(item)

	for (const path of dateFields) {
		const raw = getByPath(result, path)
		if (Object.prototype.toString.call(raw) === '[object Date]') {
			setByPath(result, path, convertToApiTimeDate(raw))
		}
	}

	return result
}

function getByPath(obj, path) {
	return path.split('.').reduce((acc, key) => acc?.[key], obj)
}

function setByPath(obj, path, value) {
	const keys = path.split('.')
	const lastKey = keys.pop()
	if (!lastKey)
		return

	let cur = obj
	for (const key of keys) {
		if (!cur[key] || typeof cur[key] !== 'object')
			return
		cur = cur[key]
	}

	cur[lastKey] = value
}




/* Create resource item
* resource: resource name
* payload: Object with item properties
*/
export async function createResourceItem(
	resource,
	user,
	pass,
	payload
) {
	const url = `${API_URL}/${resource}`
	
	const headers = new Headers()
	headers.append('Content-Type', 'application/json')
	headers.append('Authorization', 'Basic ' + Base64.encode(user + ":" + pass))

	// Request 
	return fetch(url, {
		method: "POST",
		mode: "cors",
		cache: "no-cache",
		headers: headers,
		body: JSON.stringify(payload),
	}).then(response => {
		if (response.ok) {
			return response.json()
		} else if (response.status != 200) {
			throw new Error(`on fetch ${url}: ${response.statusText}`)
		}
	}).then((data) => {
		return data[resourceSchema.id.key]
	})
}

/* Read resource item
* resource: resource name
* item: resource item name or id
* requestArguments: Array of argument, like ["key=value","key2=value2"]
* max_items: read items limit (for version items 'version=all')
*/
export async function readResourceItem(
	resource,
	item,
	user,
	pass,
	requestArguments=[],
	max_items=10000
) {
	let resourceUrl = `${API_URL}/${resource}/${item}`
	let items = []
	let response = {}

	// Read all resource pages
	// Page count limitation with cycle
	for (let index = 0; index < max_items / API_PAGE_ITEMS_COUNT + 1; index++) {
		let url = resourceUrl
		const page = index + 1
		// Add arguments
		if (requestArguments.length > 0 || page > 1) {
			url += url.includes('?') ? '&' : '?'
			for (const arg of requestArguments) {
				url += arg + '&'
			}

			// If second or higer page
			if (page > 1) {
				url += `page=${page}&`
			}

			url = url.slice(0, -1)
		}
		
		// Request
		response = await fetch(url, {
			method: 'GET',
			mode: "cors",
			cache: "no-cache",
			headers: {
				'Authorization': 'Basic ' + Base64.encode(user + ":" + pass),
			}
		}).then(response => {
			if (response.ok) {
				return response.json()
			} else {
				throw new Error(
					`on GET resource ${resource}: ${response.statusText}`,
					{ cause: response },
				)
			}
		}).then(payload =>{
			items = items.concat(payload?._items ?? [])
			return payload
		})

		// There is next page present
		if (response?._links && response?._links.next) {
			continue
		} else {
			break
		}
	}

	response._items = items
	return response
}


/* Read resource items list
* resource: resource name
* requestArguments: Array of argument, like ["key=value","key2=value2"]
* max_items: read items limit (may be resized to fit the page)
*/ 
export async function readResources(
	resource,
	user,
	pass,
	requestArguments=[],
	max_items=10000
) {
	let resourceUrl = `${API_URL}/${resource}`
	let items = []

	// console.log(`readResources(resource=${resource}, max_items=${max_items}`, 'requestArguments = ', requestArguments)
	
	// Read all resource pages
	// Page count limitation with cycle
	for (let index = 0; index < max_items / API_PAGE_ITEMS_COUNT + 1; index++) {
		let url = resourceUrl
		const page = index + 1
		// Add arguments
		if (requestArguments.length > 0 || page > 1) {
			url += url.includes('?') ? '&' : '?'
			
			for (const arg of requestArguments) {
				url += arg + '&'
			}

			// If second or higer page
			if (page > 1) {
				url += `page=${page}&`
			}

			url = url.slice(0, -1)
		}

		// Request
		const response = await fetch(
			url,
			{
				method: 'GET',
				cache: "no-cache",
				headers: {
					'Authorization': 'Basic ' + Base64.encode(user + ":" + pass),
				}
			}
		).then(response => {
			if (response.ok) {
				return response.json()
			} else {
				throw new Error(`on GET resources ${resource}: ${response.statusText}`)
			}
		}).then(payload =>{
			items = items.concat(payload._items)
			return payload
		})

		// There is next page present
		if (response._links && response._links.next) {
			continue
		} else {
			break
		}
	}

	return items
}


/* Update resource item
* resource: resource name
* itemId: resource item id
* etag: _etag of item
* payload: Object with new item properties
*/
export async function updateResourceItem(
	resource,
	user,
	pass,
	itemId,
	etag,
	payload
) {
	let url = `${API_URL}/${resource}/${itemId}`

	const headers = new Headers()
	headers.append('Content-Type', 'application/json')
	headers.append('If-Match', etag)
	headers.append('Authorization', 'Basic ' + Base64.encode(user + ":" + pass))

	const body = JSON.stringify(payload);
		
	// Request
	return await fetch(url, {
		method: "PATCH",
		mode: "cors",
		cache: "no-cache",
		headers: headers,
		body: body,
	}).then(response => {
		if (response.ok) {
			return response.json()
		} else {
			throw new Error(`on PATCH resource ${resource}: ${response.statusText}`)
		}
	});
}

/* Delete resource item
*
*/
export async function deleteResourceItem(
	resource,
	user,
	pass,
	itemId,
	etag
) {
	let url = `${API_URL}/${resource}/${itemId}`

	const headers = new Headers()
	headers.append('Content-Type', 'application/json')
	headers.append('If-Match', etag)
	headers.append('Authorization', 'Basic ' + Base64.encode(user + ":" + pass))
		
	// Request
	return await fetch(url, {
		method: "DELETE",
		mode: "cors",
		cache: "no-cache",
		headers: headers,
	}).then(response => {
		if (response.ok) {
			return true
		} else {
			throw new Error(
				`on DELETE resource ${resource}/${itemId}: ${response.statusText}`)
		}
	});
}
